import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, Pause, Play, Plus, Trash2, UsersRound } from "lucide-react";
import Layout from "@/components/Layout";
import { EmptyState, LoadingPanel } from "@/components/Bits";
import { Pill } from "@/components/Music";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useConfirm } from "@/components/Confirm";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, formatDate } from "@/lib/api";
import { useRemembered } from "@/lib/useRemembered";
import { cn } from "@/lib/utils";
import { fetchResetLink, ResetLinkDialog, restoreLogin, SuspendDialog } from "@/components/Login";

const inputCls = "w-full h-10 rounded-lg border border-line bg-card px-3 text-[13.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
const ROLE_TEXT = {
  admin: "Everything, including settings and the team",
  staff: "Edits the catalog, writers and registrations",
  writer: "Sees only their own works, read-only",
};

export default function Team() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [rows, setRows] = useRemembered("team");
  const [writers, setWriters] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff", writer_id: "" });
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const [suspending, setSuspending] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/users").then(({ data }) => setRows(data)).catch((e) => toast.error(apiError(e)));
  }, [setRows]);
  useEffect(() => {
    load();
    api.get("/writers").then(({ data }) => setWriters(data)).catch(() => {});
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", { ...form, writer_id: form.role === "writer" ? form.writer_id : null });
      toast.success("Account created — pass on the email and password; they'll choose their own at first sign-in");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "staff", writer_id: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const setRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.user_id}/role`, { role });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const resetLink = async (u) => {
    const l = await fetchResetLink(u);
    if (l) setLink(l);
  };
  const remove = async (u) => {
    if (!(await confirm({ title: `Remove ${u.name}?`, body: "Their login is deleted for good. To block access for now, suspend instead.", confirmText: "Remove", destructive: true }))) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Layout
      title="Team"
      subtitle="Who can sign in to this workspace"
      action={
        <Button className="bg-brand hover:bg-brand-hover" onClick={() => setOpen(true)}>
          <Plus className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Add person</span>
        </Button>
      }
    >
      {!rows ? (
        <LoadingPanel />
      ) : rows.length === 0 ? (
        <div className="panel">
          <EmptyState icon={UsersRound} title="Just you so far" body="Add staff to help keep the catalog, or give a writer a read-only login from the Writers page." />
        </div>
      ) : (
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="bg-surface/70 border-b border-line">
                  {["Person", "Role", "Last signed in", ""].map((h) => (
                    <th key={h} className="t-head text-left px-4 py-2.5 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.user_id} className={cn("t-row", u.suspended && "bg-rose-50/60")}>
                    <td className="t-cell">
                      <div className="font-semibold flex items-center gap-2">
                        {u.name}
                        {u.suspended && <Pill tone="bad">Suspended</Pill>}
                      </div>
                      <div className="text-[12px] text-muted2">{u.email}</div>
                      {u.suspended && u.suspend_reason && <div className="text-[11.5px] text-muted2 mt-0.5">Reason: {u.suspend_reason}</div>}
                    </td>
                    <td className="t-cell">
                      {u.role === "writer" ? (
                        <Pill tone="brand">Writer · {u.writer_name}</Pill>
                      ) : (
                        <select value={u.role} disabled={u.user_id === user.user_id} onChange={(e) => setRole(u, e.target.value)} className="h-8 rounded-lg border border-line bg-card px-2 text-[13px]" aria-label="Role">
                          <option value="admin">Admin</option>
                          <option value="staff">Staff</option>
                        </select>
                      )}
                      {u.must_change_password && <span className="ml-2 text-[11.5px] text-gold">Hasn't set a password yet</span>}
                    </td>
                    <td className="t-cell text-[12.5px]">{u.last_login_at ? formatDate(u.last_login_at) : "Never"}</td>
                    <td className="t-cell">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => resetLink(u)} title="Password reset link" aria-label="Password reset link" className="p-1.5 rounded-lg text-muted2 hover:bg-brand-light hover:text-brand">
                          <KeyRound className="w-4 h-4" />
                        </button>
                        {u.user_id !== user.user_id &&
                          (u.suspended ? (
                            <button onClick={() => restoreLogin(u, load)} title="Restore login" aria-label="Restore login" className="p-1.5 rounded-lg text-stock-out hover:bg-brand-light hover:text-brand">
                              <Play className="w-4 h-4" />
                            </button>
                          ) : (
                            <button onClick={() => setSuspending(u)} title="Suspend login" aria-label="Suspend login" className="p-1.5 rounded-lg text-muted2 hover:bg-rose-50 hover:text-stock-out">
                              <Pause className="w-4 h-4" />
                            </button>
                          ))}
                        {u.user_id !== user.user_id && (
                          <button onClick={() => remove(u)} title="Remove" aria-label="Remove" className="p-1.5 rounded-lg text-muted2 hover:bg-rose-50 hover:text-stock-out">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Add someone</DialogTitle>
          </DialogHeader>
          <form id="add-user" onSubmit={create} className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {["admin", "staff", "writer"].map((r) => (
                <button
                  type="button"
                  key={r}
                  onClick={() => setForm({ ...form, role: r })}
                  aria-pressed={form.role === r}
                  className={`rounded-xl border p-2.5 text-left ${form.role === r ? "border-brand bg-brand-light" : "border-line"}`}
                >
                  <div className="text-[13px] font-semibold capitalize">{r}</div>
                  <div className="text-[11px] text-muted2 mt-0.5 leading-snug">{ROLE_TEXT[r]}</div>
                </button>
              ))}
            </div>
            {form.role === "writer" && (
              <select
                required
                className={inputCls}
                value={form.writer_id}
                onChange={(e) => {
                  const w = writers.find((x) => x.writer_id === e.target.value);
                  setForm({ ...form, writer_id: e.target.value, name: w?.name || form.name, email: form.email || w?.email || "" });
                }}
                aria-label="Which writer"
              >
                <option value="">Which writer?</option>
                {writers
                  .filter((w) => !w.login)
                  .map((w) => (
                    <option key={w.writer_id} value={w.writer_id}>
                      {w.name}
                    </option>
                  ))}
              </select>
            )}
            <input className={inputCls} required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className={inputCls} required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input className={inputCls} required minLength={8} placeholder="Temporary password (8+)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </form>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="add-user" disabled={busy} className="bg-brand hover:bg-brand-hover">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResetLinkDialog link={link} onClose={() => setLink(null)} />
      <SuspendDialog target={suspending} onClose={() => setSuspending(null)} onDone={load} />
    </Layout>
  );
}
